<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/css";
import ChevronLeft from "@/components/icons/ChevronLeft.vue";
import ChevronRight from "@/components/icons/ChevronRight.vue";
import Close from "@/components/icons/Close.vue";
import Plus from "@/components/icons/Plus.vue";
import Minus from "@/components/icons/Minus.vue";
import gsap from "gsap";

const props = defineProps({
	images: {
		type: Array,
		required: true,
	},
	initialIndex: {
		type: Number,
		default: 0,
	},
	isOpen: {
		type: Boolean,
		required: true,
	},
	panSpeed: {
		type: Number,
		default: 0.4,
	},
	zoomScale: {
		type: Number,
		default: 2,
	},
});

const emit = defineEmits(["close"]);

const slider = ref(null);
const cursorRef = ref(null);
let cursorXTo, cursorYTo;
const zoomed = ref(false);
const cursorVisible = ref(false);
const pointerStart = { x: 0, y: 0 };

let activeZoomEl = null;
let panXTo = null;
let panYTo = null;

let touchStartX = 0;
let touchStartY = 0;
let panStartX = 0;
let panStartY = 0;

const onTouchStart = (e) => {
	if (e.touches.length !== 1) return;
	touchStartX = e.touches[0].clientX;
	touchStartY = e.touches[0].clientY;
	panStartX = gsap.getProperty(activeZoomEl, "x");
	panStartY = gsap.getProperty(activeZoomEl, "y");
};

const onTouchMove = (e) => {
	if (e.touches.length !== 1) return;
	e.preventDefault();

	const dx = e.touches[0].clientX - touchStartX;
	const dy = e.touches[0].clientY - touchStartY;

	const maxPanX = (activeZoomEl.offsetWidth * (props.zoomScale - 1)) / 2;
	const maxPanY = (activeZoomEl.offsetHeight * (props.zoomScale - 1)) / 2;

	gsap.set(activeZoomEl, {
		x: Math.max(-maxPanX, Math.min(maxPanX, panStartX + dx)),
		y: Math.max(-maxPanY, Math.min(maxPanY, panStartY + dy)),
	});
};

const zoomIn = (imgEl, e) => {
	activeZoomEl = imgEl;

	const rect = imgEl.getBoundingClientRect();
	const dx = e.clientX - (rect.left + rect.width / 2);
	const dy = e.clientY - (rect.top + rect.height / 2);

	const maxPanX = (rect.width * (props.zoomScale - 1)) / 2;
	const maxPanY = (rect.height * (props.zoomScale - 1)) / 2;

	const tx = Math.max(-maxPanX, Math.min(maxPanX, -dx * props.zoomScale));
	const ty = Math.max(-maxPanY, Math.min(maxPanY, -dy * props.zoomScale));

	gsap.to(imgEl, {
		scale: props.zoomScale,
		x: tx,
		y: ty,
		duration: 0.3,
		ease: "power2.out",
	});
	zoomed.value = true;
	if (slider.value) slider.value.allowTouchMove = false;
	imgEl.addEventListener("touchstart", onTouchStart);
	imgEl.addEventListener("touchmove", onTouchMove, { passive: false });
	if (pointerStart.type === "mouse") {
		panXTo = gsap.quickTo(imgEl, "x", {
			duration: props.panSpeed,
			ease: "power2.out",
		});
		panYTo = gsap.quickTo(imgEl, "y", {
			duration: props.panSpeed,
			ease: "power2.out",
		});
	}
};

const zoomOut = (animate = true) => {
	if (!activeZoomEl) return;
	activeZoomEl.removeEventListener("touchstart", onTouchStart);
	activeZoomEl.removeEventListener("touchmove", onTouchMove);
	gsap.killTweensOf(activeZoomEl);
	if (animate) {
		gsap.to(activeZoomEl, {
			scale: 1,
			x: 0,
			y: 0,
			duration: 0.3,
			ease: "power2.out",
		});
	} else {
		gsap.set(activeZoomEl, { clearProps: "x,y,scale" });
	}
	zoomed.value = false;
	activeZoomEl = null;
	panXTo = null;
	panYTo = null;
	if (slider.value) slider.value.allowTouchMove = true;
};

const onCursorMove = (e) => {
	if (!props.isOpen) return;

	if (cursorXTo && cursorYTo) {
		cursorXTo(e.clientX);
		cursorYTo(e.clientY);
	}

	if (zoomed.value && activeZoomEl) {
		const rect = activeZoomEl.parentElement.getBoundingClientRect();
		const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
		const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;

		const maxPanX = (activeZoomEl.offsetWidth * (props.zoomScale - 1)) / 2;
		const maxPanY = (activeZoomEl.offsetHeight * (props.zoomScale - 1)) / 2;

		if (panXTo) panXTo(-nx * maxPanX);
		if (panYTo) panYTo(-ny * maxPanY);
	}
};

const onPointerDown = (e) => {
	pointerStart.x = e.clientX;
	pointerStart.y = e.clientY;
	pointerStart.type = e.pointerType;
};

const onImageClick = (e) => {
	if (
		Math.abs(e.clientX - pointerStart.x) < 5 &&
		Math.abs(e.clientY - pointerStart.y) < 5
	) {
		if (zoomed.value) {
			zoomOut();
		} else {
			zoomIn(e.currentTarget, e);
		}
	}
};

const onSwiper = (swiper) => {
	slider.value = swiper;
	slider.value.slideTo(props.initialIndex);
	swiper.on("slideChange", () => zoomOut(false));
};

const closeLightbox = () => {
	zoomOut(false);
	emit("close");
};

const handleKeydown = (event) => {
	if (!props.isOpen) return;

	if (event.key === "Escape") {
		closeLightbox();
	}
};

watch(
	() => props.isOpen,
	(newValue) => {
		if (newValue) {
			nextTick(() => {
				if (slider.value && slider.value.swiper) {
					slider.value.swiper.slideTo(props.initialIndex);
				}
			});
		} else {
			zoomOut(false);
		}
	},
);

onMounted(() => {
	document.addEventListener("keydown", handleKeydown);
	window.addEventListener("mousemove", onCursorMove);
	cursorXTo = gsap.quickTo(cursorRef.value, "x", {
		duration: 0.08,
		ease: "power1.out",
	});
	cursorYTo = gsap.quickTo(cursorRef.value, "y", {
		duration: 0.08,
		ease: "power1.out",
	});
});

onUnmounted(() => {
	document.removeEventListener("keydown", handleKeydown);
	window.removeEventListener("mousemove", onCursorMove);
});
</script>

<template>
	<div
		ref="cursorRef"
		class="pointer-events-none fixed top-0 left-0 z-60 hidden transition-opacity lg:block"
		:class="{ 'opacity-0': !isOpen || !cursorVisible }"
	>
		<div class="-translate-x-1/2 -translate-y-1/2">
			<Plus v-if="!zoomed" class="size-32" />
			<Minus v-else class="size-32" />
		</div>
	</div>
	<Transition
		enter-active-class="transition-opacity duration-300 ease-out"
		enter-from-class="opacity-0"
		enter-to-class="opacity-100"
		leave-active-class="transition-opacity duration-200 ease-in"
		leave-from-class="opacity-100"
		leave-to-class="opacity-0"
	>
		<div
			v-if="isOpen"
			class="fixed inset-0 z-50 flex items-center justify-center bg-white text-black"
		>
			<button
				class="absolute top-16 right-16 z-10 flex items-center justify-center transition-opacity hover:opacity-50 sm:top-24 sm:right-24"
				@click="closeLightbox"
				aria-label="Close lightbox"
			>
				<Close class="size-24" />
			</button>

			<div
				class="md:h6 h8 font-primary-condensed absolute top-16 left-16 z-10 sm:top-24 sm:left-24"
			>
				{{ slider?.realIndex + 1 }}/{{ images.length }}
			</div>

			<div
				class="relative flex h-full w-full items-center justify-center p-16"
				@click.self="zoomOut()"
			>
				<Swiper @swiper="onSwiper" :loop="true" class="size-full">
					<SwiperSlide v-for="(image, index) in images" :key="index">
						<div
							class="flex h-full items-center justify-center overflow-hidden"
						>
							<img
								:src="image.url"
								:alt="image.alt || `Product image ${index + 1}`"
								class="h-full object-contain will-change-transform lg:cursor-none"
								@pointerdown="onPointerDown"
								@click="onImageClick"
								@mouseenter="cursorVisible = true"
								@mouseleave="cursorVisible = false"
							/>
						</div>
					</SwiperSlide>
				</Swiper>
			</div>

			<button
				v-if="images.length > 1"
				class="absolute top-1/2 left-8 z-10 hidden size-48 -translate-y-1/2 items-center justify-center transition-all hover:opacity-50 md:flex"
				@click="slider.slidePrev()"
				aria-label="Previous image"
			>
				<ChevronLeft class="size-32" />
			</button>

			<button
				v-if="images.length > 1"
				class="absolute top-1/2 right-8 z-10 hidden size-48 -translate-y-1/2 items-center justify-center transition-all hover:opacity-50 md:flex"
				@click="slider.slideNext()"
				aria-label="Next image"
			>
				<ChevronRight class="size-32" />
			</button>

			<div
				v-if="images.length > 1"
				class="absolute bottom-16 left-1/2 z-10 flex -translate-x-1/2 gap-8 sm:bottom-48"
			>
				<button
					v-for="(image, index) in images"
					:key="index"
					class="size-48 overflow-hidden border-2 transition-all hover:opacity-80"
					:class="{
						'border-black': slider?.realIndex === index,
						'border-black/10': slider?.realIndex !== index,
					}"
					@click="slider.slideTo(index)"
					:aria-label="`Go to image ${index + 1}`"
				>
					<img
						:src="image.url"
						:alt="image.alt || `Thumbnail ${index + 1}`"
						class="size-full object-cover"
					/>
				</button>
			</div>
		</div>
	</Transition>
</template>
